import { RecordFormFrame } from '@/app/work-tabs/record-form-frame';
import { useWorkTab, useWorkFieldId } from '@/app/work-tabs/work-tab-context';
import { ProductBaseUnitSelect } from './product-base-unit-select';
import { dirtyFieldsPatch } from '@/lib/form-patch';
import { CountrySelect } from '@/components/common/country-select';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  marginPct,
  moneySchema,
  pickingStrategySchema,
  productCreateSchema,
  productUpdateSchema,
  productStatusSchema,
  productTypeSchema,
  taxRateTypeSchema,
  PRODUCT_USAGE_ROLES,
  type ProductUpdateInput,
  type ProductDetail,
} from '@erp/shared';
import { CircleAlert, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, type FieldNamesMarkedBoolean, type UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FormField } from '@/components/common/form-field';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { SheetFooter } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CategorizedCustomFields,
  CustomFieldLoadState,
  useMasterCustomFields,
} from '@/features/custom-fields';
import { useCreateProduct, useUpdateProduct } from '@/features/products/api/use-products';
import { uploadProductImageFiles } from '@/features/products/api/use-product-images';
import { ProductCategoryPicker } from './product-category-picker';
import {
  decimalOrNull,
  decimalOrUndefined,
  numberOrNull,
  numberOrUndefined,
  textOrNull,
  textOrUndefined,
} from '@/features/products/lib/form-value';
import type { ApiError } from '@/lib/api-client';
import { toastApiError } from '@/lib/api-error-toast';
import { PendingProductImages, type PendingImageState } from './pending-product-images';

const TAX_RATE_BY_TYPE: Record<string, string> = { STANDARD: '19', REDUCED: '7', ZERO: '0' };

const emptyToNull = { setValueAs: textOrNull };
const emptyToUndefined = { setValueAs: textOrUndefined };
const numberOrUndefinedSetter = { setValueAs: numberOrUndefined };
const numberOrNullSetter = { setValueAs: numberOrNull };
const decimalOrNullSetter = { setValueAs: decimalOrNull };
const decimalOrUndefinedSetter = { setValueAs: decimalOrUndefined };

const productDecimalFields = [
  'taxRatePct',
  'purchasePrice',
  'listPrice',
  'minMarginPct',
  'minStock',
  'reorderPoint',
  'targetStock',
  'weightNetKg',
  'weightGrossKg',
] as const;
type ProductDecimalField = (typeof productDecimalFields)[number];

type FormValues = Omit<ProductUpdateInput, 'baseUnit'> & { baseUnit?: string };

const productTextLanguages = [
  {
    locale: 'de',
    nameField: 'name',
    descriptionField: 'description',
    nameId: 'p-name',
    descriptionId: 'p-description',
  },
  {
    locale: 'tr',
    nameField: 'nameTr',
    descriptionField: 'descriptionTr',
    nameId: 'p-name-tr',
    descriptionId: 'p-description-tr',
  },
  {
    locale: 'en',
    nameField: 'nameEn',
    descriptionField: 'descriptionEn',
    nameId: 'p-name-en',
    descriptionId: 'p-description-en',
  },
  {
    locale: 'fr',
    nameField: 'nameFr',
    descriptionField: 'descriptionFr',
    nameId: 'p-name-fr',
    descriptionId: 'p-description-fr',
  },
] as const;
type ProductTextField = (typeof productTextLanguages)[number]['nameField' | 'descriptionField'];

function pendingImageKey(file: File): string {
  return `${file.name}:${String(file.size)}:${String(file.lastModified)}`;
}

function toFormValues(
  product: ProductDetail | null,
  duplicateFrom: ProductDetail | null = null,
): FormValues {
  if (product === null && duplicateFrom !== null) {
    return {
      ...toFormValues(duplicateFrom),
      articleNumber: '',
      ean: null,
      priceTiers: duplicateFrom.priceTiers
        .filter((tier) => tier.customerId === null || tier.customerId === undefined)
        .map((tier) => ({
          priceGroupId: tier.priceGroupId,
          priceGroup: tier.priceGroup,
          currency: tier.currency,
          unitCode: tier.unitCode,
          minQuantity: tier.minQuantity,
          unitPrice: tier.unitPrice,
          discountPct: tier.discountPct,
          validFrom: tier.validFrom,
          validTo: tier.validTo,
        })),
      packagings: duplicateFrom.packagings.map((packaging) => ({
        packagingTypeId: packaging.packagingTypeId,
        isSummaryDefault: packaging.isSummaryDefault,
        isWeightDefault: packaging.isWeightDefault,
        code: packaging.code,
        name: packaging.name,
        unitCode: packaging.unitCode,
        factor: packaging.factor,
        ean: null,
        isSalesDefault: packaging.isSalesDefault,
        isPurchaseDefault: packaging.isPurchaseDefault,
        weightGrossKg: packaging.weightGrossKg,
        lengthMm: packaging.lengthMm,
        widthMm: packaging.widthMm,
        heightMm: packaging.heightMm,
        minOrderQuantity: packaging.minOrderQuantity,
        orderStepQuantity: packaging.orderStepQuantity,
        customFields: packaging.customFields,
      })),
    };
  }
  if (product === null) {
    return {
      type: 'STOCK',
      usageRoles: [],
      status: 'ACTIVE',
      name: '',
      baseUnit: 'PCE',
      taxRateType: 'STANDARD',
      taxRatePct: '19',
      currency: 'EUR',
      listPrice: '0',
      purchasePrice: '0',
      minMarginPct: '0',
      minStock: '0',
      reorderPoint: '0',
      targetStock: '0',
      leadTimeDays: 0,
      isBatchManaged: false,
      isShelfLifeManaged: false,
      pickingStrategy: 'FEFO',
      tags: [],
      customFields: {},
      categoryAssignments: [],
    };
  }

  return {
    articleNumber: product.articleNumber,
    type: product.type,
    usageRoles: product.usageRoles,
    status: product.status,
    name: product.name,
    nameEn: product.nameEn,
    nameTr: product.nameTr,
    nameFr: product.nameFr,
    description: product.description,
    descriptionEn: product.descriptionEn,
    descriptionTr: product.descriptionTr,
    descriptionFr: product.descriptionFr,
    ean: product.ean,
    manufacturerNumber: product.manufacturerNumber,
    productGroup: product.productGroup,
    baseUnit: product.baseUnit,
    taxRateType: product.taxRateType,
    taxRatePct: product.taxRatePct,
    currency: product.currency,
    listPrice: product.listPrice,
    purchasePrice: product.purchasePrice,
    minMarginPct: product.minMarginPct,
    weightNetKg: product.weightNetKg,
    weightGrossKg: product.weightGrossKg,
    commodityCode: product.commodityCode,
    countryOfOrigin: product.countryOfOrigin,
    minStock: product.minStock,
    reorderPoint: product.reorderPoint,
    targetStock: product.targetStock,
    leadTimeDays: product.leadTimeDays,
    isBatchManaged: product.isBatchManaged,
    isShelfLifeManaged: product.isShelfLifeManaged,
    shelfLifeDays: product.shelfLifeDays,
    minRemainingShelfLifeDays: product.minRemainingShelfLifeDays,
    pickingStrategy: product.pickingStrategy,
    tags: product.tags,
    customFields: product.customFields,
    categoryAssignments: product.categories.map((assignment) => ({
      categoryId: assignment.categoryId,
      isPrimary: assignment.isPrimary,
    })),
  };
}

export interface ProductFormSheetProps {
  inline?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductDetail | null;
  duplicateFrom?: ProductDetail | null;
  onSaved?: (product: ProductDetail) => void;
}

export function ProductFormSheet(props: ProductFormSheetProps): JSX.Element {
  return <EditableProductFormSheet {...props} />;
}

function EditableProductFormSheet({
  inline = false,
  open,
  onOpenChange,
  product,
  duplicateFrom = null,
  onSaved,
}: ProductFormSheetProps): JSX.Element {
  const { t } = useTranslation();
  const domId = useWorkFieldId();
  const definitions = useMasterCustomFields('PRODUCT', product?.id ?? null);
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct(product?.id ?? '');
  const isEdit = product !== null;
  const isDuplicate = !isEdit && duplicateFrom !== null;
  const copyInitialized = useRef(false);
  const wasOpen = useRef(false);
  const [activeTextLanguage, setActiveTextLanguage] = useState('de');
  const [requestedTextFocus, setRequestedTextFocus] = useState<ProductTextField | null>(null);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [createdProduct, setCreatedProduct] = useState<ProductDetail | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [imageStates, setImageStates] = useState<Record<string, PendingImageState | undefined>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<Readonly<Record<string, string>>>({});
  const uploadAbortRef = useRef<AbortController | null>(null);

  const editedFieldsRef = useRef(new Set<string>());
  const dirtyFieldsRef = useRef<Partial<Readonly<FieldNamesMarkedBoolean<FormValues>>>>({});

  const form = useForm<FormValues>({
    resolver: (values, context, options) => {
      if (isDuplicate && values.articleNumber?.trim() === duplicateFrom.articleNumber.trim()) {
        return {
          values: {},
          errors: { articleNumber: { type: 'custom', message: t('duplication.differentNumber') } },
        };
      }
      return isEdit
        ? zodResolver(productUpdateSchema)(
            dirtyFieldsPatch(values, dirtyFieldsRef.current, editedFieldsRef.current),
            context,
            options,
          )
        : zodResolver(productCreateSchema)(
            { ...values, ...(isDuplicate ? { duplicateSourceId: duplicateFrom.id } : {}) },
            context,
            options,
          );
    },
    defaultValues: toFormValues(product, duplicateFrom),
  });
  dirtyFieldsRef.current = form.formState.dirtyFields;
  const setValue = useCallback<UseFormSetValue<FormValues>>(
    (name, value, options) => {
      if (options?.shouldDirty) editedFieldsRef.current.add(name.split('.')[0] ?? name);
      form.setValue(name, value, options);
    },
    [form],
  );

  useEffect(() => {
    const alreadyOpen = wasOpen.current;
    wasOpen.current = open;
    if (open) {
      if (alreadyOpen && form.formState.isDirty) return;
      if (isDuplicate && copyInitialized.current) return;
      editedFieldsRef.current.clear();
      form.reset(toFormValues(product, duplicateFrom));
      copyInitialized.current = isDuplicate;
      setCustomFieldErrors({});
      setActiveTextLanguage('de');
      setRequestedTextFocus(null);
    } else {
      copyInitialized.current = false;
      setPendingImages([]);
      setCreatedProduct(null);
      setImageProgress(0);
      setImageStates({});
      uploadAbortRef.current?.abort();
      uploadAbortRef.current = null;
    }
  }, [open, product, duplicateFrom, isDuplicate, form]);

  useEffect(() => {
    if (requestedTextFocus === null) return;
    form.setFocus(requestedTextFocus);
    setRequestedTextFocus(null);
  }, [activeTextLanguage, requestedTextFocus, form]);

  const focusTextField = (field: ProductTextField): void => {
    const language = productTextLanguages.find(
      (entry) => entry.nameField === field || entry.descriptionField === field,
    );
    if (language === undefined) return;
    setActiveTextLanguage(language.locale);
    setRequestedTextFocus(field);
  };

  const customFields = form.watch('customFields') ?? {};
  const tags = form.watch('tags') ?? [];
  const categoryAssignments = form.watch('categoryAssignments') ?? [];
  const usageRoles = form.watch('usageRoles') ?? [];
  const isBatchManaged = form.watch('isBatchManaged') === true;
  const listPrice = moneySchema.safeParse(form.watch('listPrice'));
  const purchasePrice = moneySchema.safeParse(form.watch('purchasePrice'));
  const margin =
    listPrice.success && purchasePrice.success
      ? marginPct(listPrice.data, purchasePrice.data)
      : null;

  useEffect(() => {
    if (!open || isEdit || definitions.data === undefined) return;
    const current = form.getValues('customFields') ?? {};
    const next = { ...current };
    let changed = false;
    for (const definition of definitions.data.definitions) {
      if (next[definition.key] === undefined && definition.defaultValue !== undefined) {
        next[definition.key] = definition.defaultValue;
        changed = true;
      }
    }
    if (changed) setValue('customFields', next);
  }, [definitions.data, form, open, product, isEdit, setValue]);

  const onError = (error: ApiError): void => {
    form.setError('root', {
      type: 'server',
      message: t(error.message, { defaultValue: error.message }),
    });
    if (error.code === 'PRODUCT_NUMBER_TAKEN') {
      form.setError('articleNumber', { type: 'server', message: t('duplication.numberTaken') });
      form.setFocus('articleNumber');
    }
    for (const issue of error.details ?? []) {
      if (issue.path === 'articleNumber') {
        form.setError('articleNumber', {
          type: 'server',
          message: t(issue.message, { defaultValue: issue.message }),
        });
        form.setFocus('articleNumber');
      }
    }
    setCustomFieldErrors(
      Object.fromEntries(
        (error.validationErrors ?? []).map((issue) => [issue.fieldKey, issue.reason]),
      ),
    );
    toastApiError(error, t('products.saveError'));
  };

  const finishSave = (saved: ProductDetail): void => {
    toast.success(isEdit ? t('products.updated') : t('products.created'), {
      description: saved.name,
    });
    workTab.update({ dirty: false, busy: false });
    if (!inline || onSaved === undefined) onOpenChange(false);
    onSaved?.(saved);
  };

  const uploadAndFinish = async (saved: ProductDetail): Promise<void> => {
    if (pendingImages.length === 0) {
      finishSave(saved);
      return;
    }
    setCreatedProduct(saved);
    setUploadingImages(true);
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    const failed: File[] = [];
    for (let index = 0; index < pendingImages.length; index += 1) {
      const file = pendingImages[index];
      if (file === undefined) continue;
      const key = pendingImageKey(file);
      setImageStates((current) => ({
        ...current,
        [key]: { status: 'UPLOADING', progress: 0 },
      }));
      try {
        await uploadProductImageFiles(saved.id, {
          files: [file],
          altText: saved.name,
          signal: controller.signal,
          onProgress: (fileProgress) => {
            setImageStates((current) => ({
              ...current,
              [key]: { status: 'UPLOADING', progress: fileProgress },
            }));
            setImageProgress(
              Math.round(((index + fileProgress / 100) / pendingImages.length) * 100),
            );
          },
        });
        setImageStates((current) => ({
          ...current,
          [key]: { status: 'SUCCESS', progress: 100 },
        }));
      } catch {
        failed.push(file);
        setImageStates((current) => ({
          ...current,
          [key]: { status: 'FAILED', progress: 0 },
        }));
      }
    }
    uploadAbortRef.current = null;
    setUploadingImages(false);
    setPendingImages(failed);
    if (failed.length > 0) {
      toast.error(t('products.images.partialUploadError', { count: failed.length }));
      if (!inline) onSaved?.(saved);
      return;
    }
    finishSave(saved);
  };

  const onSubmit = form.handleSubmit(
    (values) => {
      setCustomFieldErrors({});
      if (createdProduct !== null) {
        void uploadAndFinish(createdProduct);
        return;
      }
      if (isEdit) {
        updateProduct.mutate(productUpdateSchema.parse(values), {
          onSuccess: (saved) => void uploadAndFinish(saved),
          onError,
        });
        return;
      }
      createProduct.mutate(
        productCreateSchema.parse({
          ...values,
          ...(isDuplicate ? { duplicateSourceId: duplicateFrom.id } : {}),
        }),
        {
          onSuccess: (saved) => void uploadAndFinish(saved),
          onError,
        },
      );
    },
    (invalidErrors) => {
      for (const language of productTextLanguages) {
        for (const field of [language.nameField, language.descriptionField]) {
          if (invalidErrors[field] !== undefined) {
            focusTextField(field);
            return;
          }
        }
      }
      const decimalField = productDecimalFields.find((field) => invalidErrors[field] !== undefined);
      if (decimalField !== undefined) form.setFocus(decimalField);
    },
  );

  const isPending = createProduct.isPending || updateProduct.isPending || uploadingImages;
  const workTab = useWorkTab({ dirty: open && form.formState.isDirty, busy: open && isPending });
  const { update: updateWorkTab } = workTab;
  useEffect(() => () => updateWorkTab({ dirty: false, busy: false }), [updateWorkTab]);

  const errors = form.formState.errors;
  const decimalError = (field: ProductDecimalField): string | undefined => {
    if (errors[field] === undefined) return undefined;
    if (field === 'taxRatePct' || field === 'minMarginPct')
      return t('products.decimals.percentError');
    if (field === 'listPrice' || field === 'purchasePrice')
      return t('products.decimals.moneyError');
    return t('products.decimals.quantityError');
  };
  const decimalInputProps = (field: ProductDecimalField, id: string) => ({
    inputMode: 'decimal' as const,
    'aria-invalid': errors[field] !== undefined,
    'aria-describedby': [
      'p-decimal-hint',
      errors[field] === undefined ? null : `${id}-error`,
      field === 'listPrice' && margin !== null ? 'p-list-description' : null,
    ]
      .filter((value) => value !== null)
      .map(domId)
      .join(' '),
  });
  const textError = (field: ProductTextField): string | undefined => {
    const error = errors[field];
    if (error === undefined) return undefined;
    if (field === 'name' && error.type === 'too_small') return t('products.texts.nameRequired');
    if (error.type === 'too_big') {
      return t(
        field.startsWith('name')
          ? 'products.texts.nameTooLong'
          : 'products.texts.descriptionTooLong',
      );
    }
    return error.message;
  };
  const textIssues = productTextLanguages.flatMap((language) =>
    [language.nameField, language.descriptionField].flatMap((field) => {
      const message = textError(field);
      return message === undefined ? [] : [{ locale: language.locale, field, message }];
    }),
  );

  return (
    <RecordFormFrame
      inline={inline}
      open={open}
      onOpenChange={onOpenChange}
      title={
        isDuplicate
          ? t('duplication.articleTitle')
          : isEdit
            ? t('products.editTitle')
            : t('products.createTitle')
      }
      description={t(isDuplicate ? 'duplication.articleHint' : 'products.formDescription')}
      dirty={open && form.formState.isDirty}
    >
      <form
        onChangeCapture={(event) => {
          if ('name' in event.target && typeof event.target.name === 'string')
            editedFieldsRef.current.add(event.target.name.split('.')[0] ?? event.target.name);
        }}
        onSubmit={onSubmit}
        className="flex flex-1 flex-col gap-6"
        noValidate
        onKeyDown={(event) => {
          if (
            (event.metaKey || event.ctrlKey) &&
            (event.key === 'Enter' || event.key.toLowerCase() === 's')
          ) {
            event.preventDefault();
            void onSubmit();
          }
        }}
      >
        {Object.keys(errors).length > 0 && (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>{t('products.texts.validationSummary')}</p>
            {errors.root?.message ? <p>{errors.root.message}</p> : null}
            {textIssues.length > 0 && (
              <ul className="list-inside list-disc">
                {textIssues.map((issue) => (
                  <li key={issue.field}>
                    <button
                      type="button"
                      className="text-left underline"
                      onClick={() => focusTextField(issue.field)}
                    >
                      {t(`language.${issue.locale}`)}:{' '}
                      {t(
                        issue.field.startsWith('name')
                          ? 'products.fields.name'
                          : 'products.fields.description',
                      )}{' '}
                      — {issue.message}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <section className="grid gap-4 sm:grid-cols-2">
          <h3 className="text-sm font-semibold sm:col-span-2">{t('products.sections.base')}</h3>

          <FormField
            id={domId('p-number')}
            label={t('products.fields.articleNumber')}
            required={isDuplicate}
            description={t(
              isDuplicate ? 'duplication.numberHint' : 'products.fields.articleNumberHint',
            )}
            error={
              errors.articleNumber?.message
                ? t(errors.articleNumber.message, { defaultValue: errors.articleNumber.message })
                : undefined
            }
          >
            <Input
              id={domId('p-number')}
              aria-invalid={errors.articleNumber !== undefined}
              aria-describedby={[
                domId('p-number-description'),
                ...(errors.articleNumber ? [domId('p-number-error')] : []),
              ].join(' ')}
              {...form.register('articleNumber', emptyToNull)}
            />
          </FormField>

          <FormField id={domId('p-type')} label={t('products.fields.type')} required={!isEdit}>
            <Select
              value={form.watch('type') ?? '__unknown__'}
              onValueChange={(value) =>
                setValue(
                  'type',
                  value === '__unknown__' ? null : (value as NonNullable<FormValues['type']>),
                  {
                    shouldDirty: true,
                  },
                )
              }
            >
              <SelectTrigger id={domId('p-type')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isEdit && <SelectItem value="__unknown__">{t('legacy.unknown')}</SelectItem>}
                {productTypeSchema.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`products.types.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <div className="space-y-2 sm:col-span-2">
            <Label>{t('plm.material.fields.usageRoles')}</Label>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {PRODUCT_USAGE_ROLES.map((role) => (
                <label
                  key={role}
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                >
                  <Checkbox
                    checked={usageRoles.includes(role)}
                    onCheckedChange={(checked) =>
                      setValue(
                        'usageRoles',
                        checked === true
                          ? [...usageRoles, role]
                          : usageRoles.filter((entry) => entry !== role),
                        { shouldDirty: true },
                      )
                    }
                  />
                  {t(`plm.material.roles.${role}`)}
                </label>
              ))}
            </div>
          </div>

          <Tabs
            value={activeTextLanguage}
            onValueChange={setActiveTextLanguage}
            className="sm:col-span-2"
          >
            <TabsList aria-label={t('products.texts.languageLabel')}>
              {productTextLanguages.map((language) => {
                const hasError =
                  errors[language.nameField] !== undefined ||
                  errors[language.descriptionField] !== undefined;
                return (
                  <TabsTrigger
                    key={language.locale}
                    value={language.locale}
                    aria-invalid={hasError || undefined}
                    className={hasError ? 'text-destructive' : undefined}
                  >
                    {t(`language.${language.locale}`)}
                    {hasError && <CircleAlert className="h-4 w-4" aria-hidden="true" />}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            <p className="mt-2 text-xs text-muted-foreground">{t('products.texts.optionalHint')}</p>
            {productTextLanguages.map((language) => {
              const nameError = textError(language.nameField);
              const descriptionError = textError(language.descriptionField);
              return (
                <TabsContent
                  key={language.locale}
                  value={language.locale}
                  forceMount
                  hidden={activeTextLanguage !== language.locale}
                  className="space-y-4"
                >
                  <FormField
                    id={domId(language.nameId)}
                    label={t(
                      language.locale === 'de'
                        ? 'products.fields.name'
                        : language.locale === 'en'
                          ? 'products.fields.nameEn'
                          : 'products.texts.translatedName',
                      { language: t(`language.${language.locale}`) },
                    )}
                    required={language.locale === 'de'}
                    error={nameError}
                  >
                    <Input
                      id={domId(language.nameId)}
                      aria-required={language.locale === 'de'}
                      aria-invalid={nameError !== undefined}
                      aria-describedby={
                        nameError === undefined ? undefined : `${domId(language.nameId)}-error`
                      }
                      {...form.register(
                        language.nameField,
                        language.locale === 'de' ? undefined : emptyToNull,
                      )}
                    />
                  </FormField>
                  <FormField
                    id={domId(language.descriptionId)}
                    label={t(
                      language.locale === 'de'
                        ? 'products.fields.description'
                        : 'products.texts.translatedDescription',
                      { language: t(`language.${language.locale}`) },
                    )}
                    error={descriptionError}
                  >
                    <Textarea
                      id={domId(language.descriptionId)}
                      rows={3}
                      aria-invalid={descriptionError !== undefined}
                      aria-describedby={
                        descriptionError === undefined
                          ? undefined
                          : `${domId(language.descriptionId)}-error`
                      }
                      {...form.register(language.descriptionField, emptyToNull)}
                    />
                  </FormField>
                </TabsContent>
              );
            })}
          </Tabs>

          <FormField id={domId('p-status')} label={t('products.fields.status')} required>
            <Select
              value={form.watch('status') ?? 'ACTIVE'}
              onValueChange={(value) =>
                setValue('status', value as NonNullable<FormValues['status']>, {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger id={domId('p-status')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {productStatusSchema.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`products.statuses.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            id={domId('p-ean')}
            label={t('products.fields.ean')}
            error={errors.ean?.message}
          >
            <Input id={domId('p-ean')} inputMode="numeric" {...form.register('ean', emptyToNull)} />
          </FormField>

          <FormField
            id={domId('p-manufacturer')}
            label={t('products.fields.manufacturerNumber')}
            error={errors.manufacturerNumber?.message}
          >
            <Input
              id={domId('p-manufacturer')}
              {...form.register('manufacturerNumber', emptyToNull)}
            />
          </FormField>

          <div className="space-y-2 sm:col-span-2">
            <Label>{t('products.categories.assignments')}</Label>
            <ProductCategoryPicker
              value={categoryAssignments}
              onChange={(next) => setValue('categoryAssignments', next, { shouldDirty: true })}
            />
          </div>

          <FormField
            id={domId('p-unit')}
            label={t('products.fields.baseUnit')}
            required
            error={errors.baseUnit?.message}
          >
            <ProductBaseUnitSelect
              id={domId('p-unit')}
              value={form.watch('baseUnit') ?? 'PCE'}
              enabled={open}
              onValueChange={(value) => setValue('baseUnit', value, { shouldDirty: true })}
            />
          </FormField>
        </section>

        <Separator />

        <section className="grid gap-4 sm:grid-cols-2">
          <h3 className="text-sm font-semibold sm:col-span-2">{t('products.sections.prices')}</h3>
          <p id={domId('p-decimal-hint')} className="text-xs text-muted-foreground sm:col-span-2">
            {t('products.decimals.hint')}
          </p>

          <FormField id={domId('p-tax-type')} label={t('products.fields.taxRateType')} required>
            <Select
              value={form.watch('taxRateType') ?? 'STANDARD'}
              onValueChange={(value) => {
                setValue('taxRateType', value as NonNullable<FormValues['taxRateType']>, {
                  shouldDirty: true,
                });
                setValue('taxRatePct', TAX_RATE_BY_TYPE[value] ?? '19', {
                  shouldDirty: true,
                });
              }}
            >
              <SelectTrigger id={domId('p-tax-type')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {taxRateTypeSchema.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`products.taxRateTypes.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            id={domId('p-tax-pct')}
            label={t('products.fields.taxRatePct')}
            error={decimalError('taxRatePct')}
          >
            <Input
              id={domId('p-tax-pct')}
              {...decimalInputProps('taxRatePct', 'p-tax-pct')}
              {...form.register('taxRatePct', decimalOrUndefinedSetter)}
            />
          </FormField>

          <FormField
            id={domId('p-purchase')}
            label={t('products.fields.purchasePrice')}
            error={decimalError('purchasePrice')}
          >
            <Input
              id={domId('p-purchase')}
              {...decimalInputProps('purchasePrice', 'p-purchase')}
              {...form.register('purchasePrice', decimalOrUndefinedSetter)}
            />
          </FormField>

          <FormField
            id={domId('p-list')}
            label={t('products.fields.listPrice')}
            description={
              margin === null
                ? undefined
                : t('products.fields.marginHint', { value: `${margin} %` })
            }
            error={decimalError('listPrice')}
          >
            <Input
              id={domId('p-list')}
              placeholder={t('legacy.unknown')}
              {...decimalInputProps('listPrice', 'p-list')}
              {...form.register(
                'listPrice',
                isEdit ? decimalOrNullSetter : decimalOrUndefinedSetter,
              )}
            />
          </FormField>

          <FormField
            id={domId('p-currency')}
            label={t('products.fields.currency')}
            error={errors.currency?.message}
          >
            <Input
              id={domId('p-currency')}
              maxLength={3}
              {...form.register('currency', emptyToUndefined)}
            />
          </FormField>

          <FormField
            id={domId('p-min-margin')}
            label={t('products.fields.minMarginPct')}
            error={decimalError('minMarginPct')}
          >
            <Input
              id={domId('p-min-margin')}
              {...decimalInputProps('minMarginPct', 'p-min-margin')}
              {...form.register('minMarginPct', decimalOrUndefinedSetter)}
            />
          </FormField>
        </section>

        <Separator />

        <section className="grid gap-4 sm:grid-cols-2">
          <h3 className="text-sm font-semibold sm:col-span-2">
            {t('products.sections.logistics')}
          </h3>

          <FormField
            id={domId('p-min-stock')}
            label={t('products.fields.minStock')}
            error={decimalError('minStock')}
          >
            <Input
              id={domId('p-min-stock')}
              {...decimalInputProps('minStock', 'p-min-stock')}
              {...form.register('minStock', decimalOrUndefinedSetter)}
            />
          </FormField>
          <FormField
            id={domId('p-reorder')}
            label={t('products.fields.reorderPoint')}
            error={decimalError('reorderPoint')}
          >
            <Input
              id={domId('p-reorder')}
              {...decimalInputProps('reorderPoint', 'p-reorder')}
              {...form.register('reorderPoint', decimalOrUndefinedSetter)}
            />
          </FormField>
          <FormField
            id={domId('p-target-stock')}
            label={t('products.fields.targetStock')}
            error={decimalError('targetStock')}
          >
            <Input
              id={domId('p-target-stock')}
              {...decimalInputProps('targetStock', 'p-target-stock')}
              {...form.register('targetStock', decimalOrUndefinedSetter)}
            />
          </FormField>
          <FormField
            id={domId('p-lead-time')}
            label={t('products.fields.leadTimeDays')}
            error={errors.leadTimeDays?.message}
          >
            <Input
              id={domId('p-lead-time')}
              type="number"
              {...form.register('leadTimeDays', numberOrUndefinedSetter)}
            />
          </FormField>
          <FormField
            id={domId('p-weight-net')}
            label={t('shipping.netUnit')}
            description={t('shipping.massHint')}
            error={decimalError('weightNetKg')}
          >
            <Input
              id={domId('p-weight-net')}
              {...decimalInputProps('weightNetKg', 'p-weight-net')}
              {...form.register('weightNetKg', decimalOrNullSetter)}
            />
          </FormField>
          <FormField
            id={domId('p-weight-gross')}
            label={t('shipping.grossUnit')}
            description={t('shipping.weightHint')}
            error={decimalError('weightGrossKg')}
          >
            <Input
              id={domId('p-weight-gross')}
              {...decimalInputProps('weightGrossKg', 'p-weight-gross')}
              {...form.register('weightGrossKg', decimalOrNullSetter)}
            />
          </FormField>
          <FormField
            id={domId('p-commodity')}
            label={t('products.fields.commodityCode')}
            description={t('products.fields.commodityCodeHint')}
            error={errors.commodityCode?.message}
          >
            <Input id={domId('p-commodity')} {...form.register('commodityCode', emptyToNull)} />
          </FormField>
          <FormField
            id={domId('p-origin')}
            label={t('products.fields.countryOfOrigin')}
            error={errors.countryOfOrigin?.message}
          >
            <CountrySelect
              id={domId('p-origin')}
              value={form.watch('countryOfOrigin')}
              clearable={true}
              onChange={(value) =>
                setValue('countryOfOrigin', value || null, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          </FormField>
        </section>

        <Separator />

        <section className="grid gap-4 sm:grid-cols-2">
          <h3 className="text-sm font-semibold sm:col-span-2">{t('products.sections.batches')}</h3>

          <div className="flex items-center gap-2">
            <Checkbox
              id={domId('p-batch')}
              checked={isBatchManaged}
              onCheckedChange={(checked) => {
                setValue('isBatchManaged', checked === true, { shouldDirty: true });
                if (checked !== true) {
                  setValue('isShelfLifeManaged', false, { shouldDirty: true });
                }
              }}
            />
            <Label htmlFor={domId('p-batch')} className="font-normal">
              {t('products.fields.isBatchManaged')}
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id={domId('p-shelf-life')}
              checked={form.watch('isShelfLifeManaged') === true}
              disabled={!isBatchManaged}
              onCheckedChange={(checked) =>
                setValue('isShelfLifeManaged', checked === true, { shouldDirty: true })
              }
            />
            <Label htmlFor={domId('p-shelf-life')} className="font-normal">
              {t('products.fields.isShelfLifeManaged')}
            </Label>
          </div>

          <FormField
            id={domId('p-shelf-days')}
            label={t('products.fields.shelfLifeDays')}
            description={t('products.fields.shelfLifeDaysHint')}
            error={errors.shelfLifeDays?.message}
          >
            <Input
              id={domId('p-shelf-days')}
              type="number"
              {...form.register('shelfLifeDays', numberOrNullSetter)}
            />
          </FormField>

          <FormField
            id={domId('p-min-shelf-days')}
            label={t('products.fields.minRemainingShelfLifeDays')}
            error={errors.minRemainingShelfLifeDays?.message}
          >
            <Input
              id={domId('p-min-shelf-days')}
              type="number"
              {...form.register('minRemainingShelfLifeDays', numberOrNullSetter)}
            />
          </FormField>

          <FormField id={domId('p-picking')} label={t('products.fields.pickingStrategy')}>
            <Select
              value={form.watch('pickingStrategy') ?? 'FEFO'}
              onValueChange={(value) =>
                setValue('pickingStrategy', value as NonNullable<FormValues['pickingStrategy']>, {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger id={domId('p-picking')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pickingStrategySchema.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`products.pickingStrategies.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            id={domId('p-tags')}
            label={t('products.fields.tags')}
            description={t('products.fields.tagsHint')}
            error={errors.tags?.message}
            className="sm:col-span-2"
          >
            <Input
              id={domId('p-tags')}
              defaultValue={tags.join(', ')}
              onChange={(event) =>
                setValue(
                  'tags',
                  event.target.value
                    .split(',')
                    .map((entry) => entry.trim())
                    .filter((entry) => entry.length > 0),
                  { shouldDirty: true },
                )
              }
            />
          </FormField>
        </section>

        <CustomFieldLoadState
          pending={definitions.isPending}
          error={definitions.isError}
          retry={() => void definitions.refetch()}
        />
        {definitions.data === undefined || definitions.data.definitions.length === 0 ? null : (
          <>
            <Separator />
            <CategorizedCustomFields
              recordAvailability={definitions.data?.availability}
              definitions={definitions.data.definitions}
              values={customFields}
              mode="edit"
              errors={customFieldErrors}
              idPrefix={domId('product-form-custom-field')}
              onChange={(key, value) =>
                setValue(
                  'customFields',
                  { ...form.getValues('customFields'), [key]: value },
                  { shouldDirty: true },
                )
              }
            />
          </>
        )}

        <Separator />

        <PendingProductImages
          files={pendingImages}
          onChange={setPendingImages}
          disabled={isPending}
          states={imageStates}
        />
        {uploadingImages ? (
          <div className="space-y-1" role="status" aria-live="polite">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary" style={{ width: `${String(imageProgress)}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('products.images.progress', { progress: imageProgress })}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => uploadAbortRef.current?.abort()}
            >
              {t('common.cancel')}
            </Button>
          </div>
        ) : null}

        <SheetFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {t('common.save')}
          </Button>
        </SheetFooter>
      </form>
    </RecordFormFrame>
  );
}
